import { useToast } from '@chakra-ui/react';
import { GetOcrResponse, OcrItemDto } from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import { trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import {
  getOcrPaymentProofItems,
  OCR_PAYMENT_PROOF_QUERY_KEY,
  useOcrPaymentProofItems,
  useUploadOcrPaymentProof,
} from '@diamond/sol-buyer-context';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getApiErrorMessage } from '../../payment-instruction/get-api-error-message';
import {
  capturePaymentInstructionFailure,
  PaymentInstructionTelemetryContext,
} from '../../payment-instruction/payment-instruction-sentry-context';

const OCR_POLLING_INTERVAL_MS = 3000;
const OCR_POLLING_TIMEOUT_MS = 30_000;
const OCR_UPLOAD_FALLBACK_TOAST =
  'Bukti transfer belum terunggah. Silakan coba lagi.';

type PersistedOutcome = 'terminal' | 'timeout';

interface UsePaymentProofOcrHandoffParams {
  accessToken: string;
  orderPaymentId: string;
  onPersistedOutcome: (outcome: PersistedOutcome) => void;
  telemetryContext?: PaymentInstructionTelemetryContext;
}

interface UsePaymentProofOcrHandoffResult {
  isProcessing: boolean;
  uploadFiles: (files: File[]) => Promise<void>;
}

const isTerminalOcrItem = (item: OcrItemDto) =>
  item.ocr_result !== null || item.display_error !== null;

export const usePaymentProofOcrHandoff = ({
  accessToken,
  orderPaymentId,
  onPersistedOutcome,
  telemetryContext,
}: UsePaymentProofOcrHandoffParams): UsePaymentProofOcrHandoffResult => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const uploadMutation = useUploadOcrPaymentProof(accessToken);
  const [pendingJobIds, setPendingJobIds] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const processingRef = useRef(false);
  const completionReportedRef = useRef(false);

  const { data } = useOcrPaymentProofItems(orderPaymentId, accessToken, {
    enabled: pendingJobIds.length > 0,
    refetchInterval: pendingJobIds.length > 0 ? OCR_POLLING_INTERVAL_MS : false,
  });

  const completeHandoff = useCallback(
    (outcome: PersistedOutcome) => {
      if (completionReportedRef.current) return;
      completionReportedRef.current = true;
      processingRef.current = false;
      setIsProcessing(false);
      setPendingJobIds([]);
      onPersistedOutcome(outcome);
    },
    [onPersistedOutcome]
  );

  useEffect(() => {
    if (pendingJobIds.length === 0) return;

    const currentItems = data?.items.filter((item) =>
      pendingJobIds.includes(item.id)
    );
    if (!currentItems || currentItems.length !== pendingJobIds.length) return;
    if (!currentItems.every(isTerminalOcrItem)) return;

    completeHandoff('terminal');
  }, [completeHandoff, data?.items, pendingJobIds]);

  useEffect(() => {
    if (pendingJobIds.length === 0) return undefined;

    const timeoutId = window.setTimeout(() => {
      completeHandoff('timeout');
    }, OCR_POLLING_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [completeHandoff, pendingJobIds]);

  const startPollingPersistedItems = useCallback(
    (response: GetOcrResponse, previousJobIds: Set<string>) => {
      const newItems = response.items.filter(
        (item) => !previousJobIds.has(item.id)
      );
      if (newItems.length === 0) return false;

      queryClient.setQueryData<GetOcrResponse>(
        [...OCR_PAYMENT_PROOF_QUERY_KEY, orderPaymentId],
        response
      );
      setPendingJobIds(newItems.map((item) => item.id));
      return true;
    },
    [orderPaymentId, queryClient]
  );

  const showUploadFailureToast = useCallback(
    (error?: unknown) => {
      const message =
        error === undefined
          ? OCR_UPLOAD_FALLBACK_TOAST
          : getApiErrorMessage(error, OCR_UPLOAD_FALLBACK_TOAST);
      showToast(toast, 'error', message);
    },
    [toast]
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || processingRef.current) return;

      processingRef.current = true;
      completionReportedRef.current = false;
      setIsProcessing(true);

      if (telemetryContext) {
        trackEvent(TRACKING_EVENTS.OCR_UPLOAD_STARTED, {
          entry_source: telemetryContext.source,
          ff_payment_instruction_page:
            telemetryContext.ffPaymentInstructionPage,
          order_meta_id: telemetryContext.orderMetaId,
          payment_method: telemetryContext.paymentMethod,
          platform: 'web',
          resolved_destination: 'order_detail_proof',
          warehouse: telemetryContext.warehouse,
        });
      }

      const queryKey = [...OCR_PAYMENT_PROOF_QUERY_KEY, orderPaymentId];
      let previousJobIds = new Set(
        (queryClient.getQueryData<GetOcrResponse>(queryKey)?.items ?? []).map(
          (item) => item.id
        )
      );
      let hasReliablePreUploadSnapshot = false;
      let lastUploadError: unknown;

      try {
        const preUploadResponse = await getOcrPaymentProofItems(
          accessToken,
          orderPaymentId
        );
        previousJobIds = new Set(
          preUploadResponse.items.map((item) => item.id)
        );
        hasReliablePreUploadSnapshot = true;
        queryClient.setQueryData<GetOcrResponse>(queryKey, preUploadResponse);
      } catch (error: unknown) {
        if (telemetryContext) {
          capturePaymentInstructionFailure(
            error,
            telemetryContext,
            'ocr-pre-upload-snapshot-failed'
          );
        }
        // Upload can still proceed. Without a fresh baseline, a failed upload
        // cannot be reconciled safely because an old job may look newly created.
      }

      try {
        const response = await uploadMutation.mutateAsync({
          orderPaymentId,
          files,
        });

        if (telemetryContext) {
          trackEvent(TRACKING_EVENTS.OCR_UPLOAD_COMPLETED, {
            entry_source: telemetryContext.source,
            ff_payment_instruction_page:
              telemetryContext.ffPaymentInstructionPage,
            order_meta_id: telemetryContext.orderMetaId,
            payment_method: telemetryContext.paymentMethod,
            platform: 'web',
            resolved_destination: 'order_detail_proof',
            warehouse: telemetryContext.warehouse,
          });
        }

        if (startPollingPersistedItems(response, previousJobIds)) return;
      } catch (error: unknown) {
        lastUploadError = error;
        if (telemetryContext) {
          capturePaymentInstructionFailure(
            error,
            telemetryContext,
            'ocr-upload-failed'
          );
        }
        if (!hasReliablePreUploadSnapshot) {
          processingRef.current = false;
          setIsProcessing(false);
          showUploadFailureToast(error);
          return;
        }

        try {
          const reconciledResponse = await getOcrPaymentProofItems(
            accessToken,
            orderPaymentId
          );
          if (startPollingPersistedItems(reconciledResponse, previousJobIds)) {
            return;
          }
        } catch (error: unknown) {
          if (telemetryContext) {
            capturePaymentInstructionFailure(
              error,
              telemetryContext,
              'ocr-upload-reconciliation-failed'
            );
          }
          // The query layer reports the transport failure. The UI remains retryable.
        }
      }

      processingRef.current = false;
      setIsProcessing(false);
      if (telemetryContext) {
        capturePaymentInstructionFailure(
          new Error('OCR upload did not create a persisted job'),
          telemetryContext,
          'ocr-upload-missing-persisted-job'
        );
      }
      showUploadFailureToast(lastUploadError);
    },
    [
      accessToken,
      orderPaymentId,
      queryClient,
      showUploadFailureToast,
      startPollingPersistedItems,
      telemetryContext,
      uploadMutation,
    ]
  );

  return {
    isProcessing: isProcessing || uploadMutation.isPending,
    uploadFiles,
  };
};
